// true-false-engine.js — True/False testing engine.
//
// PORT TARGET: iEnglish/Classes/KFEModules/Learning/TrueFalseContentTesting/
//              KFETrueFalseTestingBaseManager.{h,m} (Obj-C).
// Behavioural reference: ~/Desktop/ii-sg-demo-plugin/.../sg-demo-app.js
// renderTrueFalse() — captures the iOS sequencing behaviour without the
// architectural split we want here.
//
// Unity didn't port T/F, so the API drift from Obj-C → JS is wider than the
// L2B / HAAI engines. We keep the conceptual shape (match-pair selection,
// balanced match / no-match sequencing) and expose it via the same engine
// surface every other exercise uses.
//
// Pure state machine. No DOM, no fetch, no audio. The screen owns playback
// sequencing (auto-play A, reveal B, replay buttons) and reports user
// answers back via `answer(userSaidYes)`. The engine only knows which
// pair is current, whether the answer was correct, and what comes next.
//
// Public API:
//   new TrueFalseEngine(pairs, instances, tfType, options?)
//   start()              → returns first TFState (mode "instance")
//   answer(userSaidYes)  → returns TFState (mode "feedback")
//   advance()            → returns next TFState (next instance or "complete")
//   snapshot()           → current TFState
//
// Events (CustomEvent on the engine):
//   "stateChanged"   — fires whenever state advances; detail = snapshot
//   "tf_combination" — fires after each answer with the event-log payload
//                      (see docs/EVENT-LOG-SPEC-2026-05-04.md `tf_combination`)
//   "completed"      — fires when state.mode === "complete"

/**
 * @typedef {object} TFPhrase
 * @property {string} id
 * @property {{ text?: string, audioUrl?: string }} [target]
 */

/**
 * @typedef {object} TFPair
 * @property {TFPhrase} a
 * @property {TFPhrase} b
 * @property {boolean}  isMatch
 */

/** @typedef {"audio_only"|"text_only"|"audio_text"} TFType */

/**
 * @typedef {object} TFInstance
 * @property {TFPhrase} a
 * @property {TFPhrase} b
 * @property {boolean}  isMatch
 * @property {number}   index            zero-based position in the test
 * @property {number}   total            total instances scheduled
 * @property {TFPair}   correctPair      a match-pair to listen to after answer
 */

/** @typedef {"instance"|"feedback"|"complete"} TFMode */

/**
 * @typedef {object} TFState
 * @property {TFMode}    mode
 * @property {TFType}    tfType
 * @property {TFInstance|null} instance
 * @property {boolean}   wasCorrect
 * @property {boolean}   userSaidYes
 * @property {number}    index            zero-based current instance index
 * @property {number}    total
 * @property {number}    correctCount
 * @property {number}    accuracyPercent  0–100; correctCount / total * 100
 * @property {boolean}   passed           accuracyPercent >= passScore
 * @property {number}    passScore        percentage required to pass
 */

export const VALID_TF_TYPES = /** @type {const} */ (["audio_only", "text_only", "audio_text"]);

/** Default pass threshold when the item carries no mustPercent. Mirrors the
 *  iOS data-driven `playInfo.mustPercent`; 70 matches L2B Testing's default. */
const DEFAULT_PASS_SCORE = 70;

export class TrueFalseEngine extends EventTarget {
  /**
   * @param {TFPair[]} pairs
   * @param {number}   instances
   * @param {TFType}   tfType
   * @param {{ passScore?: number }} [options]
   */
  constructor(pairs, instances, tfType, options = {}) {
    super();
    /** @type {TFPair[]} */
    this._pairs = Array.isArray(pairs) ? pairs : [];
    this._instances = Math.max(0, Math.floor(instances) || 0);
    /** @type {TFType} */
    // iOS canon: KFETrueFalseTestingBaseVC.m:46-48 coerces missing/0 TFType
    // to 1 (audio_only). Brief 086 §7.1 (closing Brief 083 §7.1).
    this._tfType = /** @type {TFType} */ (
      VALID_TF_TYPES.includes(/** @type {any} */ (tfType)) ? tfType : "audio_only"
    );
    this._passScore = Number.isFinite(options.passScore)
      ? Number(options.passScore)
      : DEFAULT_PASS_SCORE;

    /** @type {TFPair[]} */
    this._matchPairs = this._pairs.filter((p) => p.isMatch);
    /** @type {TFPair[]} */
    this._noMatchPairs = this._pairs.filter((p) => !p.isMatch);

    /** @type {TFPair[]} */
    this._sequence = this._buildSequence();

    this._index = 0;
    this._correctCount = 0;
    /** @type {"instance"|"feedback"|"complete"} */
    this._mode = "instance";
    this._wasCorrect = false;
    this._userSaidYes = false;

    /** @type {TFState|null} */
    this._lastState = null;
  }

  // ─── Public API ───────────────────────────────────────────────

  /** @returns {TFState} */
  start() {
    this._index = 0;
    this._correctCount = 0;
    this._mode = this._sequence.length > 0 ? "instance" : "complete";
    return this._publish(this._buildState());
  }

  /**
   * Record the learner's Yes/No answer for the current instance.
   * Transitions to "feedback" mode. Caller should later call advance().
   *
   * @param {boolean} userSaidYes
   * @returns {TFState}
   */
  answer(userSaidYes) {
    if (this._mode !== "instance") return this._publish(this._buildState());
    const pair = this._sequence[this._index];
    if (!pair) return this._publish(this._buildState());

    this._userSaidYes = !!userSaidYes;
    this._wasCorrect = this._userSaidYes === pair.isMatch;
    if (this._wasCorrect) this._correctCount++;
    this._mode = "feedback";

    this.dispatchEvent(new CustomEvent("tf_combination", {
      detail: {
        matchboxItemId: pair.a?.id ?? "",
        mmuItemId:      pair.b?.id ?? "",
        isMatching:     !!pair.isMatch,
        completed:      this._wasCorrect,
      },
    }));

    return this._publish(this._buildState());
  }

  /**
   * Advance past the feedback screen into the next instance (or "complete").
   * @returns {TFState}
   */
  advance() {
    if (this._mode !== "feedback") return this._publish(this._buildState());
    this._index++;
    if (this._index >= this._sequence.length) {
      this._mode = "complete";
    } else {
      this._mode = "instance";
      this._wasCorrect = false;
      this._userSaidYes = false;
    }
    return this._publish(this._buildState());
  }

  /** @returns {TFState} */
  snapshot() {
    return this._lastState ?? this._buildState();
  }

  // ─── Sequence building — mirrors plugin renderTrueFalse() ────

  /**
   * Build a balanced match / no-match test sequence of length `_instances`.
   * Alternates: even index → match, odd → no-match. Falls back to whichever
   * pool has pairs left if one runs out. Final order is shuffled so the
   * alternation isn't predictable to the learner.
   *
   * @returns {TFPair[]}
   */
  _buildSequence() {
    if (this._instances <= 0) return [];
    if (this._pairs.length === 0) return [];

    /** @type {TFPair[]} */
    const out = [];
    const shuffledMatch = TrueFalseEngine._shuffle(this._matchPairs.slice());
    const shuffledNoMatch = TrueFalseEngine._shuffle(this._noMatchPairs.slice());

    let mi = 0;
    let ni = 0;
    for (let i = 0; i < this._instances; i++) {
      const wantMatch = (i % 2 === 0);
      if (wantMatch && shuffledMatch.length > 0) {
        out.push(shuffledMatch[mi++ % shuffledMatch.length]);
      } else if (shuffledNoMatch.length > 0) {
        out.push(shuffledNoMatch[ni++ % shuffledNoMatch.length]);
      } else if (shuffledMatch.length > 0) {
        out.push(shuffledMatch[mi++ % shuffledMatch.length]);
      }
    }
    return TrueFalseEngine._shuffle(out);
  }

  /**
   * Pick a matched pair to show on the feedback screen — prefer one that
   * shares phrase A with the current instance, fall back to any match. The
   * iOS app shows "Listen to phrase A and B together" on a correct answer
   * (same pair) and "Listen to a pairing that makes sense" on a wrong one
   * (the plugin reference does this with the same correctPair).
   *
   * @param {TFPair} current
   * @returns {TFPair}
   */
  _pickCorrectPair(current) {
    if (current.isMatch) return current;
    const aId = current.a?.id;
    if (aId) {
      const sameA = this._matchPairs.find((p) => p.a?.id === aId);
      if (sameA) return sameA;
    }
    return this._matchPairs[0] ?? current;
  }

  // ─── State builders ───────────────────────────────────────────

  /** @returns {number} 0–100 share of correct answers over scheduled total. */
  _accuracyPercent() {
    const total = this._sequence.length;
    return total > 0 ? (this._correctCount / total) * 100 : 0;
  }

  /** @returns {TFState} */
  _buildState() {
    const accuracyPercent = this._accuracyPercent();
    const passed = this._mode === "complete" && accuracyPercent >= this._passScore;
    if (this._mode === "complete" || this._sequence.length === 0) {
      return {
        mode: this._sequence.length === 0 ? "complete" : "complete",
        tfType: this._tfType,
        instance: null,
        wasCorrect: this._wasCorrect,
        userSaidYes: this._userSaidYes,
        index: this._index,
        total: this._sequence.length,
        correctCount: this._correctCount,
        accuracyPercent,
        passed,
        passScore: this._passScore,
      };
    }
    const pair = this._sequence[this._index];
    /** @type {TFInstance} */
    const instance = {
      a: pair.a,
      b: pair.b,
      isMatch: pair.isMatch,
      index: this._index,
      total: this._sequence.length,
      correctPair: this._pickCorrectPair(pair),
    };
    return {
      mode: this._mode,
      tfType: this._tfType,
      instance,
      wasCorrect: this._wasCorrect,
      userSaidYes: this._userSaidYes,
      index: this._index,
      total: this._sequence.length,
      correctCount: this._correctCount,
      accuracyPercent,
      passed,
      passScore: this._passScore,
    };
  }

  /**
   * @param {TFState} state
   * @returns {TFState}
   */
  _publish(state) {
    this._lastState = state;
    this.dispatchEvent(new CustomEvent("stateChanged", { detail: state }));
    if (state.mode === "complete") this.dispatchEvent(new Event("completed"));
    return state;
  }

  /**
   * @template T
   * @param {T[]} list
   * @returns {T[]}
   */
  static _shuffle(list) {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = list[i]; list[i] = list[j]; list[j] = tmp;
    }
    return list;
  }
}
